import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';

import {
  BUY, DATA_AGGREGATE_BY_METER, DATA_AGGREGATE_BY_PORTFOLIO,
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY,
  DATA_GROUP_BY_TRADE_TYPE, SELL,
} from 'src/util/constants';

import StackedBarChart from './StackedBarChart';
import {
  METER, UNIT_CARBON, UNIT_CURRENCY, UNIT_ENERGY,
} from './chartConstants';
import { compareByX } from '../helpers/chart';
import { getUnitProps, showNoDataMsg } from '../helpers/common';

/**
 * Builds a Map from numeric timestamp (`DateTime.ts`) to tooltip summary entries for a
 * single direction (buy/sell), covering every timestamp in `data` rather than just one.
 * This lets tooltip lookups on hover be an O(1) `Map.get` instead of an O(n) re-scan of
 * every meter data point for each hover.
 * @param {object} data - meter data
 * @param {string} unit - energy or carbon
 * @param {boolean} isByMeter - returns against meter level if true otherwise property level
 * @param {string} dir - sell or buy
 * @returns {Map} - Map of numeric timestamp to tooltip summary
 *   (`{x, value, flags}`, or `{[meterKey]: {x, value, flags, title, identifier}}` when isByMeter).
 */
export const buildTooltipIndex = (data, unit, isByMeter, dir) => {
  const index = new Map();

  /**
   * Indexes a single direction's timestamped data into `index`.
   * @param {object} directionData - data keyed by timestamp for one meter/direction
   * @param {Function} decorate - maps the base summary entry to its final indexed shape
   * @returns {void}
   */
  const indexDirectionData = (directionData, decorate) => {
    if (!directionData || Object.keys(directionData).length === 0) {
      return;
    }
    Object.keys(directionData).forEach((timestamp) => {
      const { timestamp: meterTimestamp } = directionData[timestamp] || {};
      if (!meterTimestamp) {
        return;
      }
      const { value, carbon, flags: flagsData } = directionData[timestamp];
      const y = unit === UNIT_ENERGY ? value : carbon;
      const flags = unit === UNIT_ENERGY ? flagsData : [];
      decorate(meterTimestamp.ts, { x: meterTimestamp, value: y, flags });
    });
  };

  if (!isByMeter) {
    indexDirectionData(data[dir], (tsValue, entry) => index.set(tsValue, entry));
    return index;
  }

  Object.keys(data).forEach((key) => {
    const { title, identifier } = data[key];
    indexDirectionData(data[key][dir], (tsValue, entry) => {
      if (!index.has(tsValue)) { index.set(tsValue, {}); }
      index.get(tsValue)[key] = { ...entry, title, identifier };
    });
  });

  return index;
};

/**
 * Looks up the tooltip data for a given timestamp from the index built by
 * {@link buildTooltipIndex}. Performs an O(1) Map lookup rather than re-scanning all
 * meter data.
 * @param {Map} index - index built by {@link buildTooltipIndex}
 * @param {object} tooltipTimestamp
 * @param {boolean} isByMeter - returns against meter level if true otherwise property level
 * @returns {object} - tooltip data for the timestamp.
 */
export const getTooltipDataFromIndex = (index, tooltipTimestamp, isByMeter) => {
  if (!tooltipTimestamp) {
    return null;
  }
  const bucket = index.get(tooltipTimestamp.ts);
  if (!isByMeter) {
    return bucket || null;
  }
  return bucket || {};
};

/**
 * Build time based meter data
 * @param {object} data - meter data
 * @param {string} unit - energy or carbon
 * @returns {object} - chart meter data.
 */
const buildChartData = (data, unit) => {
  const finalResp = {};
  if (data && Object.keys(data).length > 0) {
    Object.keys(data).forEach((timestamp) => {
      const {
        timestamp: x, value: energy, carbon,
      } = data[timestamp];
      const finalValue = unit === UNIT_ENERGY ? energy : carbon;
      if (x) {
        finalResp[timestamp] = { x, y: Number(finalValue) };
      }
    });
  }

  return finalResp;
};

/**
 * Build and returns the data for meter chart
 * @param {object} data - meter data
 * @param {string} unit - energy or carbon
 * @param {boolean} isByMeter - returns against meter level if true otherwise property level
 * @returns {object} - chart data.
 */
export const getChartData = (data, unit, isByMeter) => {
  let dataConsumed = {};
  let dataGenerated = {};
  if (isByMeter) {
    Object.keys(data)?.forEach((key) => {
      const { buy, sell } = data[key];
      const buyData = buildChartData(buy, unit);
      const sellData = buildChartData(sell, unit);
      if (buyData && Object.keys(buyData).length > 0) {
        dataConsumed = { ...dataConsumed, [key]: buyData };
      }
      if (sellData && Object.keys(sellData).length > 0) {
        dataGenerated = { ...dataGenerated, [key]: sellData };
      }
    });
  } else {
    const { buy, sell } = data;
    dataConsumed = { aggregated: buildChartData(buy, unit) };
    dataGenerated = { aggregated: buildChartData(sell, unit) };
  }
  const offset = {};
  Object.keys(dataGenerated)?.forEach((key) => {
    const dataGeneratedByMeter = dataGenerated[key];
    if (Object.keys(dataGeneratedByMeter).length > 0) {
      Object.keys(dataGeneratedByMeter).forEach((timestamp) => {
        const { x, y } = dataGenerated[key][timestamp];
        offset[x] = { x, y: -y, y0: -y };
        if (dataConsumed[key] && !(dataConsumed[key][timestamp])) {
          dataConsumed[key][timestamp] = { x, y: 0 };
        }
      });
    }
  });

  Object.keys(dataConsumed).forEach((key) => {
    const dataConsumedByMeter = dataConsumed[key];
    if (Object.keys(dataConsumedByMeter).length > 0) {
      Object.keys(dataConsumedByMeter).forEach((timestamp) => {
        const { x, y } = dataConsumed[key][timestamp];
        offset[x] = { x, y: -y, y0: -y };
        if (dataGenerated[key] && !(dataGenerated[key][timestamp])) {
          dataGenerated[key][timestamp] = { x, y: 0 };
        }
      });
    }
  });
  return { dataConsumed, dataGenerated };
};

/**
 * Checks if an object has non-empty data for a given key.
 * @param {object} data - The object to check.
 * @param {string} key - The key to check for non-empty data.
 * @returns {boolean} - True if the object has non-empty data for the given key, false otherwise.
 */
export const hasNonEmptyData = (data, key) => {
  if (!data || !key) {
    return false;
  }
  // Check if the object has the key and it's not null or an empty object
  if (Object.prototype.hasOwnProperty.call(data, key)
    && data[key] && Object.keys(data[key]).length > 0) {
    return true;
  }

  // check nested objects
  const hasNestedNonEmptyData = Object.keys(data).some((k) => data[k] && typeof data[k] === 'object' && hasNonEmptyData(data[k], key));

  if (hasNestedNonEmptyData) {
    return true;
  }
  return false;
};

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - ChartMeter component
 */
function ChartMeter({
  aggregation, chartView, data = undefined, handleChartClick,
  hoverKeys, isByMeter, selectedKeys, timespan, timezone, tooltipLabelFunc,
  tooltipUpdateFunc, unit, width,
}) {
  const hasData = Boolean(data && Object.keys(data).length > 0
    && (hasNonEmptyData(data, BUY) || hasNonEmptyData(data, SELL)));

  const {
    label: unitLabel, format: yAxisFormat,
    tooltipFormat,
  } = getUnitProps(unit);

  const { dataConsumed, dataGenerated } = useMemo(
    () => (hasData ? getChartData(data, unit, isByMeter) : { dataConsumed: {}, dataGenerated: {} }),
    [data, hasData, unit, isByMeter],
  );

  // Stable, tooltipTimestamp-independent indexes so hovering a new bar does not
  // re-scan all meter data - only an O(1) Map lookup is needed on hover.
  const tooltipConsumedIndex = useMemo(
    () => (hasData ? buildTooltipIndex(data, unit, isByMeter, 'buy') : new Map()),
    [data, hasData, unit, isByMeter],
  );
  const tooltipGeneratedIndex = useMemo(
    () => (hasData ? buildTooltipIndex(data, unit, isByMeter, 'sell') : new Map()),
    [data, hasData, unit, isByMeter],
  );

  const getTooltipDataFunc = useCallback(
    (timestamp) => ({
      tooltipConsumed: getTooltipDataFromIndex(tooltipConsumedIndex, timestamp, isByMeter),
      tooltipGenerated: getTooltipDataFromIndex(tooltipGeneratedIndex, timestamp, isByMeter),
    }),
    [tooltipConsumedIndex, tooltipGeneratedIndex, isByMeter],
  );

  const tooltipDateFormat = aggregation.match(/^PT\d+[HMS]/) ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT;

  // Logic to frame the chart data :  End

  const toolTipProps = useMemo(() => ({
    getTooltipDataFunc,
    isCarbon: unit === UNIT_CARBON,
    tooltipDateFormat,
    tooltipFormat,
    tooltipLabelFunc,
    unit,
    unitLabel,
  }), [getTooltipDataFunc, tooltipDateFormat, tooltipFormat, tooltipLabelFunc, unit, unitLabel]);

  const stackBarProps = useMemo(() => ({
    data: { sell: dataGenerated, buy: dataConsumed },
    compareByXFunc: compareByX,
    tooltipUpdateFunc,
    handleChartClick,
    chartType: METER,
    hoverKeys,
    selectedKeys,
    isByMeter,
  }), [dataGenerated, dataConsumed, handleChartClick, hoverKeys, isByMeter,
    selectedKeys, tooltipUpdateFunc]);

  if (!hasData) {
    return showNoDataMsg(METER);
  }

  return (
    <StackedBarChart
      chartView={chartView}
      stackBarProps={stackBarProps}
      timespan={timespan}
      timezone={timezone}
      toolTipProps={toolTipProps}
      width={width}
      yAxisFormat={yAxisFormat}
      stepSize={aggregation}
    />
  );
}

ChartMeter.propTypes = {
  aggregation: PropTypes.string.isRequired,
  chartView: PropTypes.shape({
    groupBy: PropTypes.oneOf([
      DATA_GROUP_BY_COUNTERPARTY,
      DATA_GROUP_BY_TRADE_TYPE,
    ]).isRequired,
    aggregateBy: PropTypes.oneOf([
      DATA_AGGREGATE_BY_METER,
      DATA_AGGREGATE_BY_PORTFOLIO,
      DATA_AGGREGATE_BY_PROPERTY,
    ]).isRequired,
  }).isRequired,
  data: PropTypes.oneOfType([PropTypes.object]),
  handleChartClick: PropTypes.func.isRequired,
  hoverKeys: PropTypes.instanceOf(Array).isRequired,
  isByMeter: PropTypes.bool.isRequired,
  selectedKeys: PropTypes.instanceOf(Array).isRequired,
  timespan: PropTypes.shape({
    start: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    finish: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  timezone: PropTypes.string.isRequired,
  tooltipLabelFunc: PropTypes.func.isRequired,
  tooltipUpdateFunc: PropTypes.func.isRequired,
  unit: PropTypes.oneOf([
    UNIT_CURRENCY,
    UNIT_ENERGY,
    UNIT_CARBON,
  ]).isRequired,
  width: PropTypes.number.isRequired,
};

export default memo(ChartMeter);
